/**
 * useInsuranceState — the policy dossier's ONE selector.
 *
 * `useAdvanceState` FOR AN `insurance_contracts` JUNCTION: it reads what is cached, shapes it into
 * an `InsuranceSnapshot` and hands that to `computeInsuranceState`, so the rail, the narrator, the
 * four progress figures and every canvas read come out of ONE derivation. The impure half is all
 * that lives here — which caches are read, when the clock ticks, and how a lazily-arriving render
 * finds its way in; the rules are in `insuranceState.ts`, which is React-free and tested.
 *
 * THE BOARD'S SLOTS ARE THE PAGE'S OWN MODEL, NOT A SECOND ONE. `useContractAgreements` at
 * `junctionScope: 'all'` already mints one INSURANCE_LOAN slot per loan on this contract, with the
 * hire purchase facts hung on every row — so the page passes its model in and this hook reads off
 * it. A second board read here would be two answers to one question, which is the shape that made
 * the old canvas grade one policy year while describing another.
 *
 * WHAT THE BOARD CANNOT ANSWER, AND EACH GETS A READ OF ITS OWN:
 *
 * - THE POLICY ITSELF. `fetchContractPolicy` is the junction carrying THIS `insurance_id` and THIS
 *   `contract_id` — the values, the certificate, the premium, the loan, its deposits and its
 *   instalments. It is enabled only on a numeric subject: the create flow has no policy to read.
 * - THE CHAIN'S LENGTH [D-B17]. A `maybeSingle()` of one junction counts one, whatever the contract
 *   carries, so the count is its own head request under its own key.
 * - THE PARTIES AND THE VEHICLE. Both instruments print the Borrower's name, address and phone and
 *   the Vehicle's model, year, colour and registration; the certificate row checks a staged document
 *   against the plate and the chassis; and `insurances.vehicle_id` is what a created policy is
 *   written over. All of that is `fetchContractSetup`'s row, under the key contract setup already
 *   holds it in — one cache entry for the two pages.
 * - THE FORWARD WEEKLY TOTAL. Every advance's repayment is ADDED to the contract instalment
 *   [D-B54], so the Advance canvas states what the driver will owe every Monday.
 *
 * THE FORWARD READ IS THE ADVANCE DOSSIER'S, UNCHANGED, AND THE EXCLUSION IS A `useMemo` OVER IT.
 * `advanceForwardKey` is one cache entry shared by both dossiers, so the query function has to stay
 * the one that page's own panel consumes — narrowing it here would hand the advance dossier a
 * forward total with an insurance loan silently missing from it. What this page needs instead is
 * that same payload with THIS policy's loan taken out, which is a derivation over the cached
 * result and never a second request.
 *
 * AND THE `loans` HALF OF THE SUM IS COMPOSED OFF THE BOARD'S SUBJECTS, NOT OFF THAT PAYLOAD —
 * `agreements.subjects.loans` and their instalment rows, which is where the advance dossier
 * composes its own half from (`useAdvanceState.ts:511-523`). What differs is the filter, and here
 * there is none: that page takes its own `loans` row out because the figure being typed IS one of
 * them, while this dossier's subject is an `insurance_loans` row and every operational advance on
 * the contract is a term of the sum. The two id spaces overlap, so a filter by this policy's loan id
 * would silently drop whichever advance happened to share its number.
 *
 * `edits` COMES IN AND THE INSTRUMENT GOES OUT, WHICH IS THE ONE PLACE THIS PARTS FROM ITS TWIN.
 * The snapshot's `instrument` is the shape the DRAFT would be published on [D-B45] — the page's
 * typed figures, not the saved rows — and it is decided by `insuranceInstrument(form,
 * otherDeposits)`, whose two arguments are the page's `edits` and a figure only this hook can read.
 * A page that composed the instrument itself would need `saved` and `otherDeposits` before it could
 * pass the answer back in, which is a cycle. So the page's edits arrive here and the composed
 * `form` and `draft` go back with everything else: one author for the patch, and no round trip
 * through a value that does not exist yet.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import type { ContractAgreementsModel } from '@/components/agreements/AgreementSlotList';
import { insuranceSlotsForLoan, type BoardSlot } from '@/components/agreements/boardSlots';
import type { AgreementRenderPayload } from '@/lib/agreements/api';
import type { InsuranceInstrument } from '@/lib/agreements/instrument';
import { driverProfileOf } from '@/lib/driverProfile';
import { businessDay } from '@/lib/format';

import { advanceForwardKey, fetchForwardFacts } from '../advance/useAdvanceState';
import { contractSetupKey, fetchContractSetup } from '../data/contract';
import type { CertificateOnFile } from '../inline/CertificateRow';
import {
    contractPolicyChainKey,
    contractPolicyKey,
    fetchContractPolicy,
    fetchPolicyChainLength,
    type ContractPolicy,
} from './data/insurancePolicy';
import {
    insuranceInstrument,
    otherInsuranceInstalments,
    willWriteInstalment,
    type WeeklyInstalmentRow,
} from './insuranceArithmetic';
import type { InsurancePolicySubject } from './insuranceCommit';
import {
    insuranceDraftOf,
    insuranceFormWith,
    type InsuranceDraft,
    type InsuranceFormValues,
    type InsuranceSavedValues,
} from './insuranceForm';
import {
    computeInsuranceState,
    resolveInsuranceLandingArea,
    type HpClass,
    type InsuranceInstalmentFact,
    type InsuranceMoneyFact,
    type InsuranceSnapshot,
    type InsuranceState,
} from './insuranceState';
import type { InsuranceArea } from './insuranceSteps';

/** How often the seal counter re-reads the clock while a finalize is genuinely outstanding. */
const SEAL_TICK_MS = 1_000;

/** What a certificate is checked against, and what a created policy row is written over. */
export interface InsuranceVehicleSubject {
    /** `insurances.vehicle_id` — NOT NULL, so a create is refused without it. */
    id: number | null;
    /** `vehicle_registrations.number`, through `vehicles.registration_id` and never `vr.id = v.id`. */
    plate: string | null;
    /** `vehicles.vin`, which is the chassis column despite the name. */
    vin: string | null;
}

// ─── Shaping the policy read ─────────────────────────────────────────────────

/**
 * A DATE column as the form's inputs hold it — `'yyyy-MM-dd'`, on the Ghana business day.
 *
 * THE SETUP CANVAS'S OWN `dateValue` (`InsuranceArea.tsx:175-178`), spelled the same way so the two
 * screens seed one policy's period with one string. `new Date('2026-04-17')` is UTC midnight read
 * back with LOCAL getters, which walks a cover date back a day on every open-and-save west of
 * Greenwich, and compounds.
 */
function dayValue(value: string | null | undefined): string | null {
    const day = businessDay(value);
    return day ? format(day, 'yyyy-MM-dd') : null;
}

/** The money off a link row's transaction — the link itself carries no amount [D-B45]. */
function moneyFact(
    row: { id: number; transaction_id: number | null } | null | undefined,
    transaction: { amount?: number | null; transaction_at?: string | null } | null | undefined,
): InsuranceMoneyFact | null {
    if (!row) return null;
    return {
        id: row.id,
        transactionId: row.transaction_id ?? null,
        amount: transaction?.amount ?? null,
        paidOn: transaction?.transaction_at ?? null,
    };
}

/** Everything the snapshot, the form and the writer read off one policy read. */
interface PolicyShape {
    premium: InsuranceMoneyFact | null;
    discount: InsuranceMoneyFact | null;
    loanId: number | null;
    deposits: InsuranceMoneyFact[];
    instalments: InsuranceInstalmentFact[];
    /**
     * Σ of the deposit rows this dossier does NOT bind — every one after the lowest-id link.
     *
     * RAW, WHICH IS THE SERVER'S OWN `SUM(t.amount)` (20260902010000:861), AND THE CONVENTION THE
     * WHOLE SCREEN NOW KEEPS. This figure decides the stored `interest_rate` [D-B45] —
     * `insuranceCommit`'s `loanValues` zeroes the rate off `draftCoversPremium` — and the same rows
     * are re-summed, unsigned, by every server reader of them: the allocator's cap
     * (20260902010000:1610), the estate and trend totals (:1804, :1818), `get_contract_insights`
     * (:1838) and `calculate_overdue_interest` (:1847). A client that normalised the sign would
     * zero a rate the server then contradicts.
     *
     * AND A CREDIT STORED THE WRONG WAY ROUND MUST NOT READ AS COVER. `Math.abs` here would turn a
     * sign-flipped row into money towards the premium and publish the year on a Payment Record the
     * server's own arithmetic does not agree it is paid for; raw, the same row reads as the visible
     * wrong-sign figure it is. The bound deposit below and the picker that seeds it read raw for
     * exactly this reason — coverage is judged on the halves ADDED TOGETHER, and two conventions
     * inside one comparison is not a comparison. The ledger stores these credits positive, so on
     * live data (loan 294, 3000 + 977) every reading coincides.
     */
    otherDeposits: number;
    saved: InsuranceSavedValues;
}

/** The empty year — the create flow, where every field starts blank and nothing has an id. */
const EMPTY_SAVED: InsuranceSavedValues = {
    loanId: null,
    premiumId: null,
    discountId: null,
    depositId: null,
    instalmentId: null,
    valuedAt: null,
    startsAt: null,
    expiresAt: null,
    premiumTransactionId: null,
    discountTransactionId: null,
    premiumAmount: null,
    interestRate: null,
    depositTransactionId: null,
    depositAmount: null,
    weeklyAmount: null,
    firstStartsAt: null,
};

const EMPTY_SHAPE: PolicyShape = {
    premium: null,
    discount: null,
    loanId: null,
    deposits: [],
    instalments: [],
    otherDeposits: 0,
    saved: EMPTY_SAVED,
};

/**
 * One policy read, in the vocabularies the rest of the dossier speaks.
 *
 * `[0]` OF EACH EMBED IS THE ROW THE READ ORDERED, AND NOT A GUESS. `fetchContractPolicy` orders
 * the loans, their instalments, their deposits and the premiums by `id` for exactly this — the
 * lowest-id deposit link and the lowest-id instalment are what this dossier binds and what
 * `update-deposit` / `update-instalment` write onto.
 *
 * THE FORM'S WEEKLY PAIR IS `insurance_loan_instalments[0]`, NOT THE RAIL'S DERIVATION. The rail
 * reads the LATEST amount and the EARLIEST start (`latestInstalment` / `firstStartsAt`), which on a
 * loan carrying a rate history are two different rows again; the boxes bind one id, because that is
 * the id the update writes both columns onto — seeding them from a derivation would rewrite one row
 * with another's figures.
 */
function shapePolicy(policy: ContractPolicy | null | undefined): PolicyShape {
    if (!policy) return EMPTY_SHAPE;

    const row = policy.policy;
    const premiumRow = row.insurance_premiums?.[0] ?? null;
    const discountRow = premiumRow?.insurance_premium_discounts?.[0] ?? null;
    const loan = policy.loans[0] ?? null;
    const depositRows = loan?.insurance_loan_deposits ?? [];
    const instalmentRows = loan?.insurance_loan_instalments ?? [];

    const premium = moneyFact(premiumRow, premiumRow?.transactions);
    const discount = moneyFact(discountRow, discountRow?.transactions);
    const deposits = depositRows
        .map((deposit) => moneyFact(deposit, deposit.transactions))
        .filter((deposit): deposit is InsuranceMoneyFact => deposit !== null);
    const instalments: InsuranceInstalmentFact[] = instalmentRows.map((instalment) => ({
        amount: instalment.amount ?? null,
        startsAt: instalment.starts_at ?? null,
    }));

    const boundDeposit = depositRows[0] ?? null;
    const boundInstalment = instalmentRows[0] ?? null;

    return {
        premium,
        discount,
        loanId: loan?.id ?? null,
        deposits,
        instalments,
        // ONE SIGN CONVENTION ACROSS BOTH HALVES, AND IT IS THE SERVER'S — see
        // `PolicyShape.otherDeposits`. These are added to the bound deposit's own raw figure, and a
        // comparison whose two terms are read differently is not a comparison.
        otherDeposits: deposits.slice(1).reduce((total, deposit) => total + (deposit.amount ?? 0), 0),
        saved: {
            loanId: loan?.id ?? null,
            premiumId: premiumRow?.id ?? null,
            discountId: discountRow?.id ?? null,
            depositId: boundDeposit?.id ?? null,
            instalmentId: boundInstalment?.id ?? null,
            valuedAt: row.valued_at ?? null,
            startsAt: dayValue(row.starts_at),
            expiresAt: dayValue(row.expires_at),
            premiumTransactionId: premiumRow?.transaction_id ?? null,
            discountTransactionId: discountRow?.transaction_id ?? null,
            premiumAmount: loan?.amount ?? null,
            interestRate: loan?.interest_rate ?? null,
            depositTransactionId: boundDeposit?.transaction_id ?? null,
            /*
             * RAW, AS THE LEDGER HOLDS IT, WHICH IS THE SERVER'S CONVENTION AND THEREFORE THE
             * FORM'S. `otherDeposits` above is read the same way and has to be — coverage is judged
             * on the two added together — and the server re-sums these very rows unsigned when it
             * resolves what was advanced (20260902010000:861). A deposit picked in this session
             * arrives through `TransactionPicker`'s second argument as the bank recorded it
             * (`TransactionPicker.tsx:161-165`), so a figure seeded from the server is the same kind
             * of number and one row cannot read two ways depending on whether it was just picked.
             *
             * This field is never written back — it exists to answer "is the premium covered", and
             * on a credit stored the wrong way round the honest answer is no.
             */
            depositAmount: boundDeposit?.transactions?.amount ?? null,
            weeklyAmount: boundInstalment?.amount ?? null,
            firstStartsAt: dayValue(boundInstalment?.starts_at),
        },
    };
}

// ─── The clock ───────────────────────────────────────────────────────────────

/** True while this policy's instrument is signed by everyone and its executed PDF is not filed. */
function sealIsRunning(slot: BoardSlot | null): boolean {
    const document = slot?.document ?? null;
    return !!document && document.status === 'COMPLETED' && !document.finalized_at;
}

/**
 * THE THIRD OF THESE, AND EACH DOSSIER KEEPS ITS OWN [F7]. `useContractSetupState.ts:70` ticks on
 * the contract's documents and `useAdvanceState.ts:232` on one advance's slot; the predicate is
 * what differs, and it is the whole of the hook that is not `setInterval`. Lifting eight lines into
 * a shared module would leave three callers importing a timer to pass it a subject each of them
 * spells differently.
 */
function useTickingClock(active: boolean): number {
    const [tick, setTick] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return;
        const timer = window.setInterval(() => setTick(Date.now()), SEAL_TICK_MS);
        return () => window.clearInterval(timer);
    }, [active]);
    return active ? tick : 0;
}

// ─── The selector ────────────────────────────────────────────────────────────

export interface InsuranceStateResult {
    state: InsuranceState;
    snapshot: InsuranceSnapshot;
    /** Every underlying read has answered. The landing redirect waits for this. */
    stateReady: boolean;
    /** Where a route-less entry lands. Meaningless until `stateReady`. */
    landingArea: InsuranceArea;
    /**
     * A NUMERIC SUBJECT WHOSE JUNCTION READ ANSWERED NULL — a policy this contract is not on.
     *
     * It is a page state and not an error: a hand-edited URL is not a failure of anything. The page
     * draws "This contract is not on that policy.", mounts no canvas and refuses every commit.
     */
    junctionMissing: boolean;
    /** The policy's saved figures, as a starting draft. Empty on `new`. */
    saved: InsuranceSavedValues;
    /** `saved` with the admin's edits patched over it. */
    form: InsuranceFormValues;
    /** The same, parsed — what the writer and the proof-read take. */
    draft: InsuranceDraft;
    /** Which instrument this DRAFT would be published on [D-B45]. */
    instrument: InsuranceInstrument;
    /** The ids an edit is written against. Null on `new`, and null while the junction is missing. */
    existing: InsurancePolicySubject | null;
    /** Σ of the deposit rows this dossier does not bind — the writer's own argument [D-B45]. */
    otherDeposits: number;
    /**
     * What the contract's hire purchase agreement is — the writer's other argument [D-B57].
     *
     * IT IS ON THE RESULT AS WELL AS ON THE SNAPSHOT for `otherDeposits`' reason: `validateDraft`
     * and the three area writers are handed it as a fact about the contract, and a page that had to
     * reach into the snapshot for it would be reading the rail to work out what to write.
     */
    hpClass: HpClass;
    /** The forward sum's other terms — this policy's own loan is never among them. */
    forward: {
        contractInstalment: number | null;
        insuranceLoanInstalments: WeeklyInstalmentRow[];
        loanInstalments: WeeklyInstalmentRow[];
    };
    /** This policy's row on the signing board, or null. */
    slot: BoardSlot | null;
    /** The instrument carries a signature: the printed figures cannot move under it [F7]. */
    frozen: boolean;
    vehicle: InsuranceVehicleSubject;
    /** The certificate already filed against this policy, and the standing of it [D-B56]. */
    certificate: CertificateOnFile;
    /** The policy read's FIRST load — the certificate row's `loading`, and the page's spinner. */
    policyLoading: boolean;
    /** The policy read answering AGAIN, which may be repointing the certificate row. */
    policyRefetching: boolean;
}

interface Options {
    contractId: number;
    /** The route's subject: an existing policy, or the create flow. */
    policyId: number | 'new';
    /** The PAGE's signing model, at `junctionScope: 'all'`. Never re-declared here. */
    agreements: ContractAgreementsModel;
    /** What the admin has typed, by field. A patch over the server's values, never a copy of them. */
    edits?: Partial<InsuranceFormValues>;
    /** Unsaved local edits, by rail row. Shown as a modifier; never counted as progress. */
    drafts?: InsuranceSnapshot['drafts'];
}

/** Nothing typed — a stable empty patch, so the form memo does not churn on every render. */
const NO_EDITS: Partial<InsuranceFormValues> = {};

export function useInsuranceState({
    contractId,
    policyId,
    agreements,
    edits = NO_EDITS,
    drafts,
}: Options): InsuranceStateResult {
    const queryClient = useQueryClient();
    const isNew = policyId === 'new';
    const subjectId = typeof policyId === 'number' ? policyId : null;

    /*
     * THE POLICY, ON THE CREATE FLOW'S OWN TERMS: `enabled` is false while the subject is the word
     * `new`, so this read is never issued and `isLoading` is false rather than pending forever —
     * which is what lets `stateReady` include it unconditionally. The `0` in the key is a placeholder
     * for an entry that cannot be fetched and that no writer names; `contractPolicyKey` takes a
     * policy id, and there is no policy.
     */
    const {
        data: policy,
        isLoading: policyLoading,
        isFetching: policyRefetching,
        isFetched: policyFetched,
    } = useQuery({
        queryKey: contractPolicyKey(contractId, subjectId ?? 0),
        queryFn: () => fetchContractPolicy(contractId, subjectId as number),
        enabled: !!contractId && subjectId !== null,
    });

    const { data: chainLength, isLoading: chainLoading } = useQuery({
        queryKey: contractPolicyChainKey(contractId),
        queryFn: () => fetchPolicyChainLength(contractId),
        enabled: !!contractId,
    });

    /**
     * THE CONTRACT'S OWN ROW, UNDER THE KEY CONTRACT SETUP ALREADY HOLDS IT IN. Same key, same
     * query function, so the two pages share one cache entry — a second fetcher under one key is
     * what once made the insurance read return whichever of two shapes mounted first.
     */
    const { data: contract, isLoading: contractLoading } = useQuery({
        queryKey: contractSetupKey(contractId),
        queryFn: () => fetchContractSetup(contractId),
        enabled: !!contractId,
    });

    const { data: forwardFacts, isLoading: forwardLoading } = useQuery({
        queryKey: advanceForwardKey(contractId),
        queryFn: () => fetchForwardFacts(contractId),
        enabled: !!contractId,
    });

    const shape = useMemo(() => shapePolicy(policy), [policy]);

    const junctionMissing = subjectId !== null && policyFetched && !policy;

    const slot = useMemo<BoardSlot | null>(
        () => insuranceSlotsForLoan(agreements.slots, shape.loanId)[0] ?? null,
        [agreements.slots, shape.loanId],
    );

    const clock = useTickingClock(sealIsRunning(slot));

    /**
     * `missing[]` for the instrument, ONCE A ROW HAS OPENED IT — read, never fetched.
     *
     * `['agreement-render', documentId]` is a lazy per-row entry and IS the shown-hash record every
     * freezing RPC compares against, so pulling it up to page level would render a document nobody
     * opened and move the hash a countersignature attests to. `ContractSetup.tsx` and
     * `useAdvanceState.ts` both read it exactly this way, and the subscription is to the CACHE
     * rather than to a query so nothing is issued.
     */
    const documentId = slot?.document?.id ?? null;
    const [renderMissing, setRenderMissing] = useState<string[] | null>(null);

    useEffect(() => {
        if (!documentId) {
            setRenderMissing((previous) => (previous === null ? previous : null));
            return;
        }
        const read = () => {
            const next =
                queryClient.getQueryData<AgreementRenderPayload>(['agreement-render', documentId])
                    ?.missing ?? null;
            setRenderMissing((previous) => (previous === next ? previous : next));
        };
        read();
        return queryClient.getQueryCache().subscribe((event) => {
            if (event.query.queryKey?.[0] === 'agreement-render') read();
        });
    }, [queryClient, documentId]);

    /**
     * THE HIRE PURCHASE FACTS, OFF THE BOARD'S OWN SLOTS AND NEVER RE-DERIVED —
     * `useAdvanceState.ts:403-450`, on this subject.
     *
     * `driverSigned` and `witnessSigned` are contract facts hung on EVERY slot by `buildBoardSlots`,
     * from the same rows `agreement_sign` reads, so this rail and the row underneath it cannot
     * disagree about the D-B55 lock. The reference is the live hire purchase DOCUMENT's own
     * designation, and where there is none, [D-B52]'s paper designation for a contract whose hire
     * purchase agreement was signed on paper.
     */
    const hpSlot = useMemo(
        () => agreements.slots.find((candidate) => candidate.docType === 'HIRE_PURCHASE') ?? null,
        [agreements.slots],
    );

    const hpReference = useMemo(() => {
        const live = hpSlot?.document?.reference_no ?? null;
        if (live) return live;
        const paperSigned =
            !!hpSlot?.legacySignedAt &&
            (hpSlot.legacyOrigin ?? 'SCANNED').toUpperCase() === 'SCANNED';
        return paperSigned ? `P-${contractId}` : null;
    }, [hpSlot, contractId]);

    /**
     * ANY SLOT WILL DO FOR THE CROSS-DOCUMENT FACTS, and that is not laziness [D-B18]. They are
     * facts about the CONTRACT, derived once by `buildBoardSlots` and hung identically on every row
     * it mints — so whichever answers, it answers the same. This policy's own row is preferred only
     * so the read is stable while the board is still filling in.
     */
    const anySlot = slot ?? hpSlot ?? agreements.slots[0] ?? null;

    /**
     * WHAT THE CONTRACT'S HIRE PURCHASE AGREEMENT IS [D-B57] — the fourth cross-document fact, read
     * off `anySlot` exactly as the driver's and the witness's signatures are.
     *
     * `failed` IS ASKED FIRST, AND IT IS THE WHOLE OF THIS. `buildBoardSlots` takes the documents
     * read as `documents ?? []`, so a board whose DOCUMENTS read was refused still mints slots —
     * and `masterOnPaper`'s second limb, "no live hire purchase document", is then computed over an
     * empty list and answers true of every contract carrying a signed scan. Asked the other way
     * round, a failed read would silently promote contracts to the class where the weekly recovery
     * is optional. `'UNREAD'` says the question was not answered, which is the truth.
     */
    const hpClass = useMemo<HpClass>(() => {
        if (agreements.failed) return 'UNREAD';
        return anySlot?.masterOnPaper ? 'PAPER' : 'NOT_PAPER';
    }, [agreements.failed, anySlot]);

    /** The parties and the vehicle as both instruments print them. */
    const parties = useMemo(() => {
        const profile = driverProfileOf(contract?.drivers?.profiles ?? null);
        const vehicleRow = contract?.vehicles ?? null;
        return {
            driver: {
                fullName:
                    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() ||
                    null,
                phone: profile?.phone ?? null,
                address: profile?.address ?? null,
            },
            vehicle: {
                make: vehicleRow?.make ?? null,
                model: vehicleRow?.model ?? null,
                registration: vehicleRow?.vehicle_registrations?.number ?? null,
                /*
                 * TRUNCATED, WHICH IS THE RESOLVER'S OWN `to_char(trunc(...), 'FM9999')`
                 * (20260902010000:999-1000): `to_char` rounds, so a fractional value in that column
                 * would print as the FOLLOWING year on a signed page.
                 */
                year:
                    vehicleRow?.manufactured_at === null ||
                    vehicleRow?.manufactured_at === undefined
                        ? null
                        : Math.trunc(vehicleRow.manufactured_at),
                colour: vehicleRow?.color ?? null,
            },
            subject: {
                id: vehicleRow?.id ?? contract?.vehicle_id ?? null,
                plate: vehicleRow?.vehicle_registrations?.number ?? null,
                vin: vehicleRow?.vin ?? null,
            } satisfies InsuranceVehicleSubject,
        };
    }, [contract]);

    const saved = shape.saved;
    const form = useMemo(() => insuranceFormWith(saved, edits), [saved, edits]);
    const draft = useMemo(() => insuranceDraftOf(form), [form]);

    /**
     * THE SHAPE THIS SAVE WOULD PUBLISH, ASKED OF THE FORM AND NOT OF THE SAVED ROWS [D-B45].
     *
     * A payment linked but not yet saved has already made the weekly recovery contradictory, and a
     * rail still asking for one would be asking for a figure the save is about to drop.
     */
    const instrument = useMemo(
        () => insuranceInstrument(form, shape.otherDeposits),
        [form, shape.otherDeposits],
    );

    /**
     * AND WHETHER IT WOULD WRITE A WEEKLY RECOVERY, asked of the same boxes [D-B57].
     *
     * `insuranceInstrument` above already reads this limb on its way to the instrument; the rail
     * needs it on its own, because an advance with no weekly is settled at the end of the contract
     * and that is a different statement from which of the two documents the year is published on.
     */
    const writesInstalment = useMemo(() => willWriteInstalment(form), [form]);

    /**
     * AND WHETHER THE YEAR FINANCES ANYTHING AT ALL, asked of the premium box [D-B57].
     *
     * `advanceStatements`' OWN FIRST QUESTION, read off the parsed draft so that the screen and the
     * writer take the box through one parser. 54 live junctions carry cover and no loan, and a rail
     * that stated an advance's repayment arrangement on one of those would be setting terms over
     * money nobody borrowed.
     */
    const writesLoan = draft.premiumAmount !== null;

    const policyFacts = useMemo(
        () =>
            policy
                ? {
                    id: policy.policy.id,
                    valuedAt: policy.policy.valued_at ?? null,
                    startsAt: policy.policy.starts_at ?? null,
                    expiresAt: policy.policy.expires_at ?? null,
                    certificateUrl: policy.policy.certificate_url ?? null,
                    certificateAttestedAt: policy.policy.certificate_attested_at ?? null,
                }
                : null,
        [policy],
    );

    const frozen = !!slot?.document?.frozen_at;

    const snapshot = useMemo<InsuranceSnapshot>(
        () => ({
            contractId,
            now: clock ? new Date(clock) : new Date(),
            isNew,
            policy: policyFacts,
            premium: shape.premium,
            discount: shape.discount,
            loan:
                shape.loanId === null
                    ? null
                    : {
                        id: shape.loanId,
                        amount: saved.premiumAmount,
                        interestRate: saved.interestRate,
                    },
            deposits: shape.deposits,
            instalments: shape.instalments,
            instrument,
            writesInstalment,
            writesLoan,
            hpClass,
            chainLength: chainLength ?? 1,
            driver: parties.driver,
            vehicle: parties.vehicle,
            hpReference,
            hpDriverSigned: anySlot?.driverSigned ?? false,
            witnessSigned: anySlot?.witnessSigned ?? false,
            slot: slot
                ? {
                    legacySignedAt: slot.legacySignedAt,
                    legacyUrl: slot.legacyUrl,
                    legacyOrigin: slot.legacyOrigin,
                    instrument: slot.instrument,
                    masterOnPaper: slot.masterOnPaper,
                    document: slot.document
                        ? {
                            id: slot.document.id,
                            status: slot.document.status,
                            finalizedAt: slot.document.finalized_at,
                            missing: renderMissing,
                            signatures: [...slot.document.signatures]
                                .sort((a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0))
                                .map((signature) => ({
                                    party: signature.party,
                                    status: signature.status,
                                    signedAt: signature.signed_at,
                                })),
                        }
                        : null,
                    blockedReason: slot.blockedReason,
                }
                : null,
            renderMissing,
            frozen,
            drafts,
        }),
        [
            contractId,
            clock,
            isNew,
            policyFacts,
            shape,
            saved,
            instrument,
            writesInstalment,
            writesLoan,
            hpClass,
            chainLength,
            parties,
            hpReference,
            anySlot,
            slot,
            renderMissing,
            frozen,
            drafts,
        ],
    );

    const state = useMemo(() => computeInsuranceState(snapshot), [snapshot]);

    /**
     * THE OTHER YEARS' RECOVERIES — every insurance loan instalment on the contract except this
     * policy's own, over the payload the advance dossier's read cached.
     *
     * THE EXCLUSION IS THE WHOLE POINT (`forwardWeeklyTotal`'s contract): the typed figure IS this
     * policy's contribution, and leaving its saved rate in the list would count the old rate beside
     * the new one on every edit.
     */
    const forward = useMemo(
        () => ({
            contractInstalment: forwardFacts?.contractInstalment ?? null,
            insuranceLoanInstalments: otherInsuranceInstalments(
                forwardFacts?.insuranceLoanInstalments ?? [],
                shape.loanId,
            ),
            /*
             * THE `loans` HALF IS LEFT WHOLE, AND THAT IS NOT AN OVERSIGHT. This dossier's subject
             * is an `insurance_loans` row; every standing operational advance on the contract is a
             * different term of the sum, and none of them is the figure being typed. The two id
             * spaces overlap, so filtering this list by the policy's loan id would silently drop
             * whichever advance happened to share its number.
             */
            loanInstalments: (agreements.subjects?.loans ?? []).flatMap((row) =>
                (row.instalments ?? []).map((instalment) => ({
                    loanId: row.id,
                    amount: instalment.amount ?? null,
                    startsAt: instalment.starts_at ?? null,
                })),
            ),
        }),
        [forwardFacts, shape.loanId, agreements.subjects],
    );

    /**
     * THE IDS AN EDIT IS WRITTEN AGAINST — and there are none until the junction has answered.
     *
     * A commit composed while the read was still in flight would be an update of `policyId` with a
     * junction id of `undefined`, which is the shape that once sent `insurance_contract_id: null`
     * into a NOT NULL column. `junctionMissing` is the other half: a policy this contract is not on
     * has no subject either, and every bar on the page is refused.
     */
    const existing = useMemo<InsurancePolicySubject | null>(
        () =>
            policy
                ? {
                    policyId: policy.policy.id,
                    junctionId: policy.junctionId,
                    loanId: saved.loanId,
                    premiumId: saved.premiumId,
                    discountId: saved.discountId,
                    depositId: saved.depositId,
                    instalmentId: saved.instalmentId,
                    valuedAt: saved.valuedAt,
                }
                : null,
        [policy, saved],
    );

    const certificate = useMemo<CertificateOnFile>(
        () => ({
            url: policy?.policy.certificate_url ?? null,
            attestedAt: policy?.policy.certificate_attested_at ?? null,
            attestedBy: policy?.policy.certificate_attested_by ?? null,
        }),
        [policy],
    );

    const stateReady =
        !agreements.loading &&
        !policyLoading &&
        !chainLoading &&
        !contractLoading &&
        !forwardLoading;

    return {
        state,
        snapshot,
        stateReady,
        landingArea: resolveInsuranceLandingArea(state),
        junctionMissing,
        saved,
        form,
        draft,
        instrument,
        existing,
        otherDeposits: shape.otherDeposits,
        hpClass,
        forward,
        slot,
        frozen,
        vehicle: parties.subject,
        certificate,
        policyLoading,
        policyRefetching,
    };
}
